/* -------------------- theme.js -------------------- */
import { useState, useEffect, useCallback } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Paper,
  Button,
  Container,
  Stack,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  useMediaQuery,
  Snackbar,
  Alert,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

import { AdministracionReservas } from "./components/AdministracionReservas"
import { ControlDisponibilidad } from "./components/ControlDisponibilidad"
import { GestionHabitaciones } from "./components/GestionHabitaciones"
import { Reservas } from "./components/Reservas"
import { LoginForm } from "./Login"
import WeddingsAdmin from "./components/WeddingsAdmin/WeddingsAdmin"
import { GestionCodigosDescuento } from "./components/GestionCodigosDescuento"
import DashboardGuestAssignment from './components/DashboardGuestAssignment'; // 🆕 NUEVO IMPORT
import PaymentStatusTracker from './components/PaymentStatusTracker';
import AvailabilityChatBot from './components/AvailabilityChatBot';
import SignedCheckinsPage from './components/SignedCheckinsPage';

import TokenExpirationWarning from './components/TokenExpirationWarning'; // 🆕 NUEVO: Token expiration warning
import SessionTimer from './components/SessionTimer'; // 🆕 NUEVO: Session timer display

import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import GroupAddIcon from '@mui/icons-material/GroupAdd'; // 🆕 NUEVO ICONO
import PaidIcon from '@mui/icons-material/Paid';
import ArticleIcon from '@mui/icons-material/Article';
import SmartToyIcon from '@mui/icons-material/SmartToy';

// Import auth service
import { 
  getAuthUser, 
  clearAuthData, 
  logout as authLogout,
  isTokenExpired,
} from '../../services/authService';

// Tema claro con blanco de fondo, y acento #d4af37
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#d4af37',
      contrastText: '#000',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#000000',
      secondary: '#333333',
    },
  },
});

/* -------------------- Dashboard.jsx (componente padre) -------------------- */
export default function Dashboard() {
  const [user, setUser] = useState(() => {
    // Check if token is expired on initial load
    if (isTokenExpired()) {
      clearAuthData();
      return null;
    }
    return getAuthUser();
  }); // {email, role, name}
  const [view, setView] = useState(null); // key: 'gestion','disponibilidad','adminReservas','reservas'
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  function handleLogin(u) {
    setUser(u);
    // vista por defecto según rol
    if (u.role === 'admin') setView('gestion');
    else setView('adminReservas');
  }

  function handleLogout() {
    setUser(null);
    setView(null);
    authLogout();
  }

  // Listen for token expiration events
  useEffect(() => {
    const handleTokenExpired = () => {
      setUser(null);
      setView(null);
      setSnackbar({
        open: true,
        message: 'Tu sesión ha expirado. Por favor inicia sesión de nuevo.',
        severity: 'warning',
      });
    };

    const handleLogoutEvent = () => {
      setUser(null);
      setView(null);
    };

    const handleShowMessage = (event) => {
      setSnackbar({
        open: true,
        message: event.detail.message,
        severity: event.detail.severity || 'info',
      });
    };

    window.addEventListener('auth:tokenExpired', handleTokenExpired);
    window.addEventListener('auth:logout', handleLogoutEvent);
    window.addEventListener('auth:showMessage', handleShowMessage);

    return () => {
      window.removeEventListener('auth:tokenExpired', handleTokenExpired);
      window.removeEventListener('auth:logout', handleLogoutEvent);
      window.removeEventListener('auth:showMessage', handleShowMessage);
    };
  }, []);

  // Close snackbar
  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // items visibles según rol (sin sidebar)
  const adminItems = [
    { key: 'gestion', label: 'Gestión de habitaciones', icon: <MeetingRoomIcon /> },
    { key: 'disponibilidad', label: 'Control de disponibilidad', icon: <CalendarMonthIcon /> },
    { key: 'adminReservas', label: 'Administración de reservas', icon: <ManageAccountsIcon /> },
    { key: 'weddings', label: 'Bodas / Visitas', icon: <CalendarMonthIcon /> },
    { key: 'codigosDescuento', label: 'Códigos de Descuento', icon: <LocalOfferIcon /> },
    { key: 'guestAssignment', label: 'Asignación de Huéspedes', icon: <GroupAddIcon /> }, // 🆕 NUEVO ITEM
    { key: 'controlPagos', label: 'Control de Pagos', icon: <PaidIcon /> },
    { key: 'chatBot', label: 'Buscador', icon: <SmartToyIcon /> },
    { key: 'signedCheckins', label: 'Check-ins firmados', icon: <ArticleIcon /> },
    { key: 'reservas', label: 'Reservas', icon: <BookOnlineIcon /> },
  ];

  const employeeItems = [
    { key: 'adminReservas', label: 'Administración de reservas', icon: <ManageAccountsIcon /> },
    { key: 'codigosDescuento', label: 'Códigos de Descuento', icon: <LocalOfferIcon /> },
    { key: 'signedCheckins', label: 'Check-ins firmados', icon: <ArticleIcon /> },
    { key: 'reservas', label: 'Reservas', icon: <BookOnlineIcon /> },
  ];

  const visibleItems = user?.role === 'admin' ? adminItems : employeeItems;

  function renderView() {
    switch (view) {
      case 'gestion':
        return <GestionHabitaciones />;
      case 'weddings':
        return <WeddingsAdmin />;
      case 'disponibilidad':
        return <ControlDisponibilidad />;
      case 'adminReservas':
        return <AdministracionReservas />;
      case 'codigosDescuento':
        return <GestionCodigosDescuento />;
      case 'guestAssignment': // 🆕 NUEVO CASO
        return <DashboardGuestAssignment />;
      case 'controlPagos':
        return <PaymentStatusTracker />;
      case 'chatBot':
        return <AvailabilityChatBot />;
      case 'signedCheckins':
        return <SignedCheckinsPage />;
      case 'reservas':
        return <Reservas />;
      default:
        return (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5">Bienvenido</Typography>
            <Typography sx={{ mt: 1 }}>{user ? `Hola ${user.name || user.email}. Selecciona una opción.` : 'Por favor inicia sesión.'}</Typography>
          </Paper>
        );
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <AppBar position="static" color="transparent" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              {/* Hamburger en mobile */}
              {user && isMobile && (
                <IconButton edge="start" color="primary" onClick={() => setDrawerOpen(true)}>
                  <MenuIcon />
                </IconButton>
              )}
              <Typography variant="h6" noWrap sx={{ fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
                La Capilla Hotel — Dashboard
              </Typography>
            </Stack>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              {user ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  {/* Session Timer */}
                  <SessionTimer onExpired={handleLogout} />
                  <AccountCircleIcon />
                  <Typography noWrap sx={{ display: { xs: 'none', sm: 'block' }, maxWidth: 180 }}>
                    {user.name || user.email} ({user.role})
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<LogoutIcon />}
                    onClick={handleLogout}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap', minWidth: 'auto', px: { xs: 1, sm: 2 } }}
                  >
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Cerrar sesión</Box>
                  </Button>
                </Stack>
              ) : null}
            </Box>
          </Toolbar>

          {/* Menu horizontal desktop — una sola línea con scroll */}
          {user && !isMobile && (
            <Box
              sx={{
                px: 2,
                py: 1,
                overflowX: 'auto',
                '&::-webkit-scrollbar': { height: 4 },
                '&::-webkit-scrollbar-thumb': { bgcolor: '#d4af37', borderRadius: 2 },
              }}
            >
              <Stack direction="row" spacing={0} sx={{ flexWrap: 'nowrap', width: 'max-content' }}>
                {visibleItems.map(it => (
                  <Button
                    key={it.key}
                    startIcon={it.icon}
                    onClick={() => setView(it.key)}
                    sx={{
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      borderBottom: view === it.key ? '2px solid #d4af37' : '2px solid transparent',
                      borderRadius: 0,
                      px: 1.5,
                      py: 0.5,
                      fontSize: '0.85rem',
                      minWidth: 'auto',
                    }}
                  >
                    {it.label}
                  </Button>
                ))}
              </Stack>
            </Box>
          )}
        </AppBar>

        {/* Drawer lateral para mobile */}
        {user && (
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            PaperProps={{ sx: { width: 280, bgcolor: '#fff' } }}
          >
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontFamily: 'Georgia, serif', color: '#d4af37', fontWeight: 'bold' }}>
                La Capilla Hotel
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user.name || user.email}
              </Typography>
            </Box>
            <List>
              {visibleItems.map(it => (
                <ListItem key={it.key} disablePadding>
                  <ListItemButton
                    selected={view === it.key}
                    onClick={() => { setView(it.key); setDrawerOpen(false); }}
                    sx={{
                      '&.Mui-selected': { bgcolor: 'rgba(212,175,55,0.12)', borderRight: '3px solid #d4af37' },
                      '&.Mui-selected:hover': { bgcolor: 'rgba(212,175,55,0.18)' },
                    }}
                  >
                    <ListItemIcon sx={{ color: view === it.key ? '#d4af37' : 'text.secondary', minWidth: 40 }}>
                      {it.icon}
                    </ListItemIcon>
                    <ListItemText primary={it.label} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<LogoutIcon />}
                onClick={() => { handleLogout(); setDrawerOpen(false); }}
                sx={{ textTransform: 'none' }}
              >
                Cerrar sesión
              </Button>
            </Box>
          </Drawer>
        )}

        <Container sx={{ py: 4 }}>
          {!user ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <LoginForm onLogin={handleLogin} />
            </Box>
          ) : (
            <Box>
              {renderView()}
              {/* Token Expiration Warning Component */}
              <TokenExpirationWarning />
            </Box>
          )}
        </Container>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}